import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

/**
 * Extracts route from notification data structure
 */
function extractRouteFromNotification(action) {
  // Try multiple paths for route
  const route =
    action?.notification?.data?.route ||
    action?.data?.route ||
    action?.notification?.route ||
    action?.route ||
    action?.notification?.data?.click_action ||
    action?.data?.click_action;

  return route;
}

/**
 * Checks for initial notification when app opens from killed state
 * @param {(route: string) => void} onRoute - Called with the notification's route
 */
export async function checkInitialNotification(onRoute) {
  try {
    // Check if there's a pending notification from when app was killed
    const result = await FirebaseMessaging.getInitialNotification();

    if (result?.notification) {
      const route = extractRouteFromNotification(result);
      if (route) {
        onRoute(route);
        return true; // Indicate we found and processed a notification
      }
    } else if (result) {
      // Sometimes the structure might be different
      const route = extractRouteFromNotification(result);
      if (route) {
        onRoute(route);
        return true;
      }
    }
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Registers the push notification tap listener. Call this on app load (before login)
 * so that when the user opens the app from a notification while logged out,
 * we can store the route and redirect after login.
 * @param {(route: string) => void} onRoute - Called with the notification's route; can store it and/or navigate
 * @returns Promise<{firebaseHandle, pushHandle}> - Handles to remove listeners
 */
export async function setupPushNotificationClick(onRoute) {
  // Check for initial notification first (when app opens from killed state)
  await checkInitialNotification(onRoute);

  // Listen to Firebase Messaging notificationActionPerformed
  const firebaseHandle = FirebaseMessaging.addListener(
    'notificationActionPerformed',
    (action) => {
      const route = extractRouteFromNotification(action);
      if (route) {
        onRoute(route);
      }
    },
  );

  // Also listen to PushNotifications actionPerformed (alternative event)
  const pushHandle = PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action) => {
      const route = extractRouteFromNotification(action);
      if (route) {
        onRoute(route);
      }
    },
  );

  return {
    firebaseHandle,
    pushHandle,
    remove: () => {
      firebaseHandle?.remove?.();
      pushHandle?.remove?.();
    },
  };
}

export async function initPush(onToken) {
  const permStatus = await PushNotifications.requestPermissions();
  if (permStatus.receive !== 'granted') {
    return;
  }

  await PushNotifications.register();

  PushNotifications.addListener('registrationError', (err) => {
    console.error('Push registration error', err);
  });

  // Foreground notifications (when app is open)
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log(
      '[PUSH] ========== FOREGROUND NOTIFICATION RECEIVED ==========',
    );
    // TODO: Add foreground notification handling here (e.g., show in-app notification banner,
    // extract route from notification.data?.route, handle user interaction, etc.)
  });

  const { token } = await FirebaseMessaging.getToken();
  onToken(token);

  FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
    onToken(token);
  });

  // Firebase messaging events
  FirebaseMessaging.addListener('messageReceived', (message) => {
    // TODO: Handle Firebase data messages received in foreground if needed
  });

  // Note: notificationActionPerformed is registered via setupPushNotificationClick
  // in App.jsx on load so it works even when user is not logged in (route is stored and applied after login).
}

export function getPlatform() {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (ua.includes('iphone') || ua.includes('ipad')) return 'ios';
  return 'unknown';
}
